import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, FormControlLabel, Zoom } from '@mui/material';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { mapCheckboxes } from 'src/constants/mapConstants';
import { routes } from 'src/routes';
import { getTicketList, setMapFilter } from 'src/store/actions/ticketActions';
import { Role } from 'src/store/models/Auth';
import { TicketStatus } from 'src/store/models/Ticket';
import { colors } from 'src/utils/colors';
import { sessionStorageHelper, StoragePrefix } from 'src/utils/localStorage';
import { sortByTicketStatus } from 'src/utils/ticketUtils';

import Button from '../Button/Button';
import Map, { MapsLocation } from '../Map/Map';
import ShadowBox from '../ShadowBox/ShadowBox';
import classes from './TicketMap.module.scss';

const TicketMap: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const firstUpdate = useRef(true);

  const sessLocation = sessionStorageHelper.get<MapsLocation>(
    StoragePrefix.MAP_CENTER
  );

  const ticketState = useAppSelector((state) => state.ticket);
  const user = useAppSelector((state) => state.auth.user);
  const isService = user?.role === Role.SERVICE;
  const isManufacturer = user?.role === Role.MANUFACTURER;

  const [bounds, setBounds] = useState<string>();
  const [showButton, setShowButton] = useState(false);

  const checkboxes = mapCheckboxes[user.role];
  const lat = isService
    ? user?.company?.lat
    : user?.manufacturer?.baseCompany?.lat;
  const lon = isService
    ? user?.company?.lon
    : user?.manufacturer?.baseCompany?.lon;

  const onCheckboxChange = (checked: boolean, name: TicketStatus) => {
    dispatch(setMapFilter({ ...ticketState?.mapFilter, [name]: checked }));
  };

  const currentFilters: TicketStatus[] = [
    ...(ticketState?.mapFilter?.unassigned && isManufacturer
      ? [TicketStatus.UNASSIGNED]
      : []),
    ...(ticketState?.mapFilter?.to_do ? [TicketStatus.TO_DO] : []),
    ...(ticketState?.mapFilter?.resolved ? [TicketStatus.RESOLVED] : []),
    ...(ticketState?.mapFilter?.in_progress ? [TicketStatus.IN_PROGRESS] : []),
  ];

  const getTickets = () => {
    if (isManufacturer) {
      dispatch(
        getTicketList({
          params: { size: 1000, bounds: bounds, s: currentFilters },
        })
      );
    } else {
      dispatch(
        getTicketList({
          manId: user?.manufacturer?.id,
          params: { size: 1000, bounds: bounds, s: currentFilters },
        })
      );
    }
  };

  useEffect(() => {
    if (bounds) {
      if (firstUpdate.current) {
        setShowButton(false);
        getTickets();
        firstUpdate.current = false;
      } else {
        setShowButton(true);
      }
    }
    // eslint-disable-next-line
  }, [bounds]);

  useEffect(() => {
    if (!firstUpdate.current) {
      setShowButton(true);
    }
  }, [ticketState?.mapFilter]);

  const markers = useMemo(
    () =>
      [...(ticketState?.ticketList?.data || [])]?.sort((a, b) =>
        sortByTicketStatus(a, b)
      ),
    [ticketState?.ticketList?.data]
  );

  const saveMapLocation = (map: google.maps.Map) => {
    sessionStorageHelper.set<MapsLocation>(StoragePrefix.MAP_CENTER, {
      ...sessLocation,
      zoom: map?.getZoom(),
      ...map?.getCenter().toJSON(),
    });
  };

  return (
    <div className={classes.Container}>
      <div>
        {checkboxes.map((checkbox, i) => (
          <FormControlLabel
            control={
              <Checkbox
                disableRipple
                color={checkbox.color}
                checked={ticketState?.mapFilter?.[checkbox.name]}
                onChange={(e, checked) =>
                  ((checkbox.name !== TicketStatus.TO_DO && isService) ||
                    (checkbox.name !== TicketStatus.UNASSIGNED &&
                      isManufacturer)) &&
                  onCheckboxChange(checked, checkbox.name)
                }
              />
            }
            label={checkbox.label}
            key={i}
          />
        ))}
      </div>
      <ShadowBox>
        <Map
          filter={ticketState?.mapFilter}
          center={
            sessLocation?.lat && sessLocation?.lng
              ? sessLocation
              : {
                  lat: parseFloat(lat),
                  lng: parseFloat(lon),
                }
          }
          markers={markers || []}
          zoom={sessLocation?.zoom ?? 10}
          onZoomChanged={saveMapLocation}
          onBoundsChanged={(map) => {
            saveMapLocation(map);
            setBounds(map?.getBounds().toUrlValue());
          }}
          onMarkerClick={(ticket) => {
            if (ticket.status === TicketStatus.UNASSIGNED) {
              navigate(`${routes.TICKETS}/${ticket?.id}${routes.ASSIGN}`);
            } else {
              navigate(`${routes.TICKETS}/${ticket?.id}`);
            }
          }}
        />
        <div className={classes.Search}>
          <Zoom in={showButton}>
            <Button
              startIcon={
                <FontAwesomeIcon
                  icon={faSearch}
                  color={colors()?.colorFumisGreen1}
                />
              }
              size="small"
              onClick={() => {
                setShowButton(false);
                getTickets();
              }}
            >
              {t('tickets:search_area')}
            </Button>
          </Zoom>
        </div>
      </ShadowBox>
    </div>
  );
};

export default TicketMap;
